import { Autocomplete, Box, CircularProgress, TextField, Tooltip } from "@mui/material";
import type { AutocompleteInputChangeReason } from "@mui/material/useAutocomplete";
import Grid from "@mui/material/Grid";
import type { AddressSuggestionDto } from "../../../generated/models";
import type { AddressFormValues } from "../types";
import { useAddressBookApi } from "../context";
import { formatAddressSuggestion } from "../utils/addressFormatting";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import type React from "react";

const VISUALLY_HIDDEN_STYLE = {
  position: "absolute" as const,
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden" as const,
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap" as const,
  border: 0,
};

const VALID_US_STATES = new Set([
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
  "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
  "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
  "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "DC", "AS", "GU", "MP", "PR", "VI",
]);

const ZIP_REGEX = /^\d{5}(-\d{4})?$/;

export function validateState(value: string): string | undefined {
  const trimmed = value.trim().toUpperCase();
  if (!trimmed) return "State is required";
  if (!/^[A-Z]{2}$/.test(trimmed)) return "Must be a 2-letter state code";
  if (!VALID_US_STATES.has(trimmed)) return "Invalid US state code";
  return undefined;
}

export function validateZip(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return "ZIP code is required";
  if (!ZIP_REGEX.test(trimmed)) return "Must be 5 digits or 5+4 format (e.g. 60647 or 60647-1234)";
  return undefined;
}

export interface AddressFormErrors {
  state?: string;
  zip?: string;
}

export function getAddressFormErrors(values: AddressFormValues): AddressFormErrors {
  return {
    state: validateState(values.state),
    zip: validateZip(values.zip),
  };
}

export function hasAddressFormErrors(values: AddressFormValues): boolean {
  const errors = getAddressFormErrors(values);
  return !!(errors.state || errors.zip);
}

interface AddressFormProps {
  values: AddressFormValues;
  onChange: (values: AddressFormValues) => void;
  disabled: boolean;
  showErrors?: boolean;
  onSuggestionSelected?: (suggestion: AddressSuggestionDto) => void;
}

export default function AddressForm({
  values,
  onChange,
  disabled,
  showErrors = false,
  onSuggestionSelected,
}: Readonly<AddressFormProps>) {
  const api = useAddressBookApi();
  const [debouncedStreetQuery, setDebouncedStreetQuery] = useState("");
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether the user is actively typing in the street field
  const isUserTyping = useRef(false);

  const clearPendingStreetSearch = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => { clearPendingStreetSearch(); }, [clearPendingStreetSearch]);

  const { data: lookupData, isFetching } = api.useAddressLookup(debouncedStreetQuery);
  const isSearching = isFetching && debouncedStreetQuery.trim().length >= 3;
  const streetSuggestions = useMemo(() => lookupData?.suggestions ?? [], [lookupData?.suggestions]);

  // When parent updates streetAddress externally (e.g. from AddressLookup typeahead),
  // clear the debounced search query so the Autocomplete reflects the new value
  useEffect(() => {
    if (!isUserTyping.current) {
      setDebouncedStreetQuery("");
    }
  }, [values.streetAddress]);

  const handleFieldChange =
    (field: keyof AddressFormValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized = e.target.value.replace(/[<>=]/g, "");
      onChange({ ...values, [field]: sanitized });
    };

  const handleStreetInputChange = (
    _event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === "clear") {
      clearPendingStreetSearch();
      isUserTyping.current = false;
      onChange({ ...values, streetAddress: "" });
      setDebouncedStreetQuery("");
      return;
    }

    if (reason !== "input") {
      return;
    }

    const sanitized = value.replace(/[<>=]/g, "");
    isUserTyping.current = true;
    onChange({ ...values, streetAddress: sanitized });

    clearPendingStreetSearch();
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedStreetQuery(sanitized);
      isUserTyping.current = false;
      debounceTimerRef.current = null;
    }, 300);
  };

  const handleStreetSuggestionSelected = (
    _event: React.SyntheticEvent,
    value: AddressSuggestionDto | string | null,
  ) => {
    if (value && typeof value !== "string") {
      clearPendingStreetSearch();
      isUserTyping.current = false;
      onSuggestionSelected?.(value);
      setDebouncedStreetQuery("");
    }
  };

  // Use readOnly instead of disabled so text remains at full contrast
  const inputProps = disabled
    ? { readOnly: true, sx: { cursor: "default" } }
    : {};

  const errors = showErrors ? getAddressFormErrors(values) : {};

  return (
    <Box sx={{ px: 2, pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {!disabled ? (
        <Autocomplete<AddressSuggestionDto, false, false, true>
          freeSolo
          autoHighlight
          selectOnFocus
          options={streetSuggestions}
          inputValue={values.streetAddress}
          onInputChange={handleStreetInputChange}
          onChange={handleStreetSuggestionSelected}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : (option.streetAddress ?? "")
          }
          filterOptions={(x) => x}
          loading={isSearching}
          noOptionsText={
            values.streetAddress.trim().length >= 3
              ? "No suggestions found"
              : "Type at least 3 characters"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Street Address"
              required
              fullWidth
              size="small"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching && <CircularProgress color="inherit" size={16} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li key={key} {...rest}>
                <Tooltip title={formatAddressSuggestion(option)} placement="right" arrow>
                  <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {option.streetAddress}
                    </Box>
                    <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                      {option.city}, {option.state} {option.zip}
                    </Box>
                  </Box>
                </Tooltip>
              </li>
            );
          }}
        />
      ) : (
        <TextField
          label="Street Address"
          value={values.streetAddress}
          fullWidth
          size="small"
          slotProps={{ input: inputProps }}
        />
      )}

      <TextField
        label="Street Address 2"
        value={values.streetAddress2}
        onChange={handleFieldChange("streetAddress2")}
        fullWidth
        size="small"
        slotProps={{ input: inputProps }}
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="City"
            value={values.city}
            onChange={handleFieldChange("city")}
            required
            fullWidth
            size="small"
            slotProps={{ input: inputProps }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="State"
            value={values.state}
            onChange={handleFieldChange("state")}
            required
            fullWidth
            size="small"
            error={!!errors.state}
            helperText={errors.state}
            slotProps={{ input: inputProps }}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <TextField
            label="ZIP"
            value={values.zip}
            onChange={handleFieldChange("zip")}
            required
            fullWidth
            size="small"
            error={!!errors.zip}
            helperText={errors.zip}
            slotProps={{ input: inputProps }}
          />
        </Grid>
      </Grid>

      <TextField
        label="Site Name"
        value={values.siteName}
        onChange={handleFieldChange("siteName")}
        fullWidth
        size="small"
        slotProps={{ input: inputProps }}
      />

      {/* Screen reader announcement for suggestions */}
      <div style={VISUALLY_HIDDEN_STYLE} aria-live="polite">
        {streetSuggestions.length > 0 && !isSearching
          ? `${streetSuggestions.length} address suggestions available`
          : ""}
      </div>
    </Box>
  );
}
